import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { journeyService } from '../services/journeyService';
import type { JourneyItemRewardStats, JourneyItemType } from '../services/journeyTypes';
import {
    JOURNEY_ITEM_TYPE_COLORS,
    JOURNEY_ITEM_TYPE_LABELS,
    JOURNEY_ITEM_TYPES,
} from '../utils/journeyItemTypeDefaults';

interface BulkItemRewardsUpdateProps {
    onUpdated: () => void;
}

interface RowDraft {
    brainPoints: string;
    energyCost: string;
}

const formatRange = (min: number, max: number): string => {
    if (min === max) return String(min);
    return `${min}–${max}`;
};

const buildDraftsFromStats = (
    statsMap: Record<JourneyItemType, JourneyItemRewardStats>,
): Record<JourneyItemType, RowDraft> => {
    return Object.fromEntries(
        JOURNEY_ITEM_TYPES.map((type) => {
            const stats = statsMap[type];
            return [
                type,
                {
                    brainPoints: stats.count === 0 ? '0' : String(stats.maxBrainPoints),
                    energyCost: stats.count === 0 ? '0' : String(stats.maxEnergyCost),
                },
            ];
        }),
    ) as Record<JourneyItemType, RowDraft>;
};

const parseNonNegativeInt = (value: string): number | null => {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
};

export const BulkItemRewardsUpdate = ({ onUpdated }: BulkItemRewardsUpdateProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [statsMap, setStatsMap] = useState<Record<JourneyItemType, JourneyItemRewardStats> | null>(null);
    const [drafts, setDrafts] = useState<Record<JourneyItemType, RowDraft> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    const loadStats = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const stats = await journeyService.getItemRewardStatsByType();
            setStatsMap(stats);
            setDrafts(buildDraftsFromStats(stats));
            setHasLoaded(true);
        } catch (error) {
            console.error('Failed to load item reward stats:', error);
            setLoadError(error instanceof Error ? error.message : 'Failed to load stats.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen && !hasLoaded && !isLoading) {
            loadStats();
        }
    }, [isOpen, hasLoaded, isLoading, loadStats]);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };

    const handleDraftChange = (type: JourneyItemType, field: keyof RowDraft, value: string) => {
        setDrafts((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [type]: {
                    ...prev[type],
                    [field]: value,
                },
            };
        });
    };

    const isRowUnchanged = (type: JourneyItemType): boolean => {
        if (!statsMap || !drafts) return true;
        const stats = statsMap[type];
        if (stats.count === 0) return true;

        const brainPoints = parseNonNegativeInt(drafts[type].brainPoints);
        const energyCost = parseNonNegativeInt(drafts[type].energyCost);
        if (brainPoints === null || energyCost === null) return true;

        return stats.minBrainPoints === brainPoints
            && stats.maxBrainPoints === brainPoints
            && stats.minEnergyCost === energyCost
            && stats.maxEnergyCost === energyCost;
    };

    const getChangedTypes = (): JourneyItemType[] => {
        if (!statsMap || !drafts) return [];
        return JOURNEY_ITEM_TYPES.filter((type) => {
            if (statsMap[type].count === 0) return false;
            if (isRowUnchanged(type)) return false;
            return parseNonNegativeInt(drafts[type].brainPoints) !== null
                && parseNonNegativeInt(drafts[type].energyCost) !== null;
        });
    };

    const hasInvalidInput = (): boolean => {
        if (!statsMap || !drafts) return false;
        return JOURNEY_ITEM_TYPES.some((type) => {
            if (statsMap[type].count === 0) return false;
            if (isRowUnchanged(type)) return false;
            return parseNonNegativeInt(drafts[type].brainPoints) === null
                || parseNonNegativeInt(drafts[type].energyCost) === null;
        });
    };

    const handleSaveAll = async () => {
        if (!statsMap || !drafts) return;

        if (hasInvalidInput()) {
            alert('Brain points and energy cost must be non-negative integers.');
            return;
        }

        const changedTypes = getChangedTypes();
        if (changedTypes.length === 0) return;

        const summaryLines = changedTypes.map((type) => {
            const stats = statsMap[type];
            const brainPoints = parseNonNegativeInt(drafts[type].brainPoints)!;
            const energyCost = parseNonNegativeInt(drafts[type].energyCost)!;
            return `• ${JOURNEY_ITEM_TYPE_LABELS[type]} (${stats.count} item(s)): ${brainPoints} brain, ${energyCost} energy`;
        });

        const confirmed = window.confirm(
            `Update the following quest types?\n\n${summaryLines.join('\n')}`,
        );
        if (!confirmed) return;

        setIsSaving(true);
        try {
            let totalUpdated = 0;
            for (const type of changedTypes) {
                const brainPoints = parseNonNegativeInt(drafts[type].brainPoints)!;
                const energyCost = parseNonNegativeInt(drafts[type].energyCost)!;
                const { updatedCount } = await journeyService.bulkUpdateItemRewardsByType(type, brainPoints, energyCost);
                totalUpdated += updatedCount;
            }
            await loadStats();
            onUpdated();
            alert(`Updated ${totalUpdated} item(s) across ${changedTypes.length} quest type(s).`);
        } catch (error) {
            console.error('Failed to bulk update item rewards:', error);
            alert(error instanceof Error ? error.message : 'Failed to update items. Check the console for details.');
        } finally {
            setIsSaving(false);
        }
    };

    const changedTypes = getChangedTypes();
    const isBusy = isLoading || isSaving;
    const canSave = changedTypes.length > 0 && !hasInvalidInput() && !isBusy;

    return (
        <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={handleToggle}
                className="w-full flex items-center justify-between gap-2 px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
            >
                <h2 className="text-xl font-bold text-blue-800">
                    Bulk Reward Update by Quest Type
                </h2>
                {isOpen ? <ChevronUp size={20} className="text-gray-500 shrink-0" /> : <ChevronDown size={20} className="text-gray-500 shrink-0" />}
            </button>

            {isOpen && (
                <div className="px-4 sm:px-6 pb-6 border-t border-gray-100">
                    {loadError && (
                        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            {loadError}
                        </div>
                    )}

                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-left text-gray-600">
                                    <th className="py-2 pr-4 font-medium">Quest Type</th>
                                    <th className="py-2 pr-4 font-medium">Count</th>
                                    <th className="py-2 pr-4 font-medium">Current Brain</th>
                                    <th className="py-2 pr-4 font-medium">Current Energy</th>
                                    <th className="py-2 pr-4 font-medium">New Brain</th>
                                    <th className="py-2 font-medium">New Energy</th>
                                </tr>
                            </thead>
                            <tbody>
                                {JOURNEY_ITEM_TYPES.map((type) => {
                                    const stats = statsMap?.[type];
                                    const draft = drafts?.[type];
                                    const count = stats?.count ?? 0;

                                    return (
                                        <tr key={type} className="border-b border-gray-100">
                                            <td className="py-3 pr-4">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${JOURNEY_ITEM_TYPE_COLORS[type]}`}>
                                                    {JOURNEY_ITEM_TYPE_LABELS[type]}
                                                </span>
                                            </td>
                                            <td className="py-3 pr-4 text-gray-700">
                                                {isLoading ? '…' : count}
                                            </td>
                                            <td className="py-3 pr-4 text-gray-700">
                                                {isLoading || !stats || count === 0
                                                    ? '—'
                                                    : formatRange(stats.minBrainPoints, stats.maxBrainPoints)}
                                            </td>
                                            <td className="py-3 pr-4 text-gray-700">
                                                {isLoading || !stats || count === 0
                                                    ? '—'
                                                    : formatRange(stats.minEnergyCost, stats.maxEnergyCost)}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={draft?.brainPoints ?? ''}
                                                    onChange={(e) => handleDraftChange(type, 'brainPoints', e.target.value)}
                                                    disabled={isBusy || count === 0}
                                                    className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                                />
                                            </td>
                                            <td className="py-3">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={draft?.energyCost ?? ''}
                                                    onChange={(e) => handleDraftChange(type, 'energyCost', e.target.value)}
                                                    disabled={isBusy || count === 0}
                                                    className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <Button
                            onClick={handleSaveAll}
                            variant="primary"
                            disabled={!canSave}
                            loading={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save All Changes'}
                        </Button>
                        {changedTypes.length > 0 && !isSaving && (
                            <span className="text-sm text-gray-600">
                                {changedTypes.length} quest type{changedTypes.length !== 1 ? 's' : ''} will be updated
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
